import React, { createContext, useContext, useState } from "react";
import RecipePopup from "../components/recipes/RecipePopup.jsx";
import EditRecipe from "../components/recipes/EditRecipe.jsx";

/**
 * Contexte pour gérer les popups de recettes globalement
 */
const RecipePopupContext = createContext();

export const useRecipePopup = () => {
  const context = useContext(RecipePopupContext);
  if (!context) {
    throw new Error("useRecipePopup must be used within a RecipePopupProvider");
  }
  return context;
};

/**
 * Provider pour les popups de recettes
 */
export const RecipePopupProvider = ({ children }) => {
  const [popupState, setPopupState] = useState({
    isOpen: false,
    recipe: null,
    user: null,
    onFavoriteToggle: null,
    onEdit: null,
    onDelete: null,
  });

  const [editPopupState, setEditPopupState] = useState({
    isOpen: false,
    recipe: null,
    recipeId: null,
    onRecipeUpdated: null,
  });

  /**
   * Ouvrir un popup de recette
   */
  const openRecipePopup = (recipe, user, callbacks = {}) => {
    setPopupState({
      isOpen: true,
      recipe,
      user,
      onFavoriteToggle: callbacks.onFavoriteToggle,
      onEdit: callbacks.onEdit,
      onDelete: callbacks.onDelete,
    });
  };

  /**
   * Fermer le popup
   */
  const closeRecipePopup = () => {
    setPopupState({
      isOpen: false,
      recipe: null,
      user: null,
      onFavoriteToggle: null,
      onEdit: null,
      onDelete: null,
    });
  };

  /**
   * Ouvrir le popup d'édition avec une recette
   */
  const openEditPopup = (recipe, onRecipeUpdated) => {
    setEditPopupState({
      isOpen: true,
      recipe,
      recipeId: null,
      onRecipeUpdated,
    });
  };

  /**
   * Ouvrir le popup d'édition par ID (popup s'affiche tout de suite, recette chargée à l'intérieur)
   */
  const openEditPopupById = (recipeId, onRecipeUpdated) => {
    setEditPopupState({
      isOpen: true,
      recipe: null,
      recipeId: recipeId ? String(recipeId) : null,
      onRecipeUpdated,
    });
  };

  /**
   * Fermer le popup d'édition
   */
  const closeEditPopup = () => {
    setEditPopupState({
      isOpen: false,
      recipe: null,
      recipeId: null,
      onRecipeUpdated: null,
    });
  };

  const value = {
    openRecipePopup,
    closeRecipePopup,
    popupState,
    openEditPopup,
    openEditPopupById,
    closeEditPopup,
  };

  return (
    <RecipePopupContext.Provider value={value}>
      {children}

      {/* Popup global rendu au niveau de l'application */}
      <RecipePopup
        recipe={popupState.recipe}
        user={popupState.user}
        isOpen={popupState.isOpen}
        onClose={closeRecipePopup}
        onFavoriteToggle={popupState.onFavoriteToggle}
        onEdit={popupState.onEdit}
        onDelete={popupState.onDelete}
      />

      {/* Popup d'édition */}
      {editPopupState.isOpen && (
        <EditRecipe
          recipe={editPopupState.recipe}
          recipeId={editPopupState.recipeId}
          onClose={closeEditPopup}
          onRecipeUpdated={editPopupState.onRecipeUpdated}
        />
      )}
    </RecipePopupContext.Provider>
  );
};
